/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-empty-function */
/* Cross-tab chat concurrency control using localStorage + heartbeat TTL */

interface ChatEntry {
  tabId: string
  lastSeen: number
  count: number
}

const STORAGE_KEY = 'pci_ai_agent_active_chats_v1'
const TAB_ID_KEY = 'pci_ai_agent_tab_id'
const HEARTBEAT_INTERVAL_MS = 10_000
const ENTRY_TTL_MS = 30_000

function getNow(): number {
  return Date.now()
}

function getTabId(): string {
  if (typeof window === 'undefined') return 'ssr'
  try {
    const existing = sessionStorage.getItem(TAB_ID_KEY)
    if (existing) return existing
  } catch {
    // ignore storage errors
  }
  const newId = `${Math.random().toString(16).slice(2)}-${getNow().toString(16)}`
  try {
    sessionStorage.setItem(TAB_ID_KEY, newId)
  } catch {
    // ignore storage errors
  }
  return newId
}

function readEntries(): ChatEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const now = getNow()
    const cutoff = now - ENTRY_TTL_MS
    if (!raw) return []
    const parsed = JSON.parse(raw) as Partial<ChatEntry>[]
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((e) => e && typeof e.tabId === 'string')
      .map((e) => ({
        tabId: e.tabId as string,
        lastSeen: Number(e.lastSeen) || 0,
        count: Math.max(0, Number(e.count) || 0),
      }))
      .filter((e) => e.lastSeen > cutoff)
  } catch {
    return []
  }
}

function writeEntries(entries: ChatEntry[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // ignore storage errors
  }
}

function getLimitFromEnv(): number {
  const raw = process.env.REACT_APP_AI_CHAT_MAX_CONCURRENT
  const parsed = raw ? Number(raw) : NaN
  if (!Number.isNaN(parsed) && parsed > 0) return Math.floor(parsed)
  return 1
}

function getActiveCount(): number {
  const entries = readEntries()
  return entries.reduce((sum, e) => sum + Math.max(0, e.count), 0)
}

function acquireChatSlot(limit = getLimitFromEnv()): { allowed: boolean; remaining: number } {
  if (typeof window === 'undefined') return { allowed: true, remaining: limit }
  const tabId = getTabId()
  const now = getNow()
  let entries = readEntries()
  const totalCount = entries.reduce((sum, e) => sum + Math.max(0, e.count), 0)
  if (totalCount >= limit) {
    const remaining = Math.max(0, limit - totalCount)
    return { allowed: false, remaining }
  }
  let hasSelf = false
  entries = entries.map((e) => {
    if (e.tabId === tabId) {
      hasSelf = true
      return { ...e, lastSeen: now, count: e.count + 1 }
    }
    return e
  })
  if (!hasSelf) entries.push({ tabId, lastSeen: now, count: 1 })
  writeEntries(entries)
  const remaining = Math.max(0, limit - (totalCount + 1))
  return { allowed: true, remaining }
}

function releaseChatSlot(): void {
  if (typeof window === 'undefined') return
  const tabId = getTabId()
  const entries = readEntries()
  const updated = entries.map((e) => {
    if (e.tabId === tabId) {
      const next = Math.max(0, (e.count || 0) - 1)
      return { ...e, count: next, lastSeen: getNow() }
    }
    return e
  })
  // If this tab has no chats left, keep a zero-count entry so heartbeat can refresh it,
  // but it won't consume a slot or be pruned until TTL expires.
  writeEntries(updated)
}

function heartbeatOnce(): void {
  const tabId = getTabId()
  const now = getNow()
  const entries = readEntries()
  let found = false
  const updated = entries.map((e) =>
    e.tabId === tabId ? ((found = true), { ...e, lastSeen: now }) : e,
  )
  if (!found) updated.push({ tabId, lastSeen: now, count: 0 })
  writeEntries(updated)
}

function startChatHeartbeat(): () => void {
  if (typeof window === 'undefined') return () => {}
  heartbeatOnce()
  const id = setInterval(heartbeatOnce, HEARTBEAT_INTERVAL_MS)
  const stop = () => clearInterval(id)
  // ensure cleanup on unload
  const onUnload = () => {
    try {
      stop()
      releaseChatSlot()
    } catch {}
  }
  window.addEventListener('beforeunload', onUnload)
  return () => {
    window.removeEventListener('beforeunload', onUnload)
    stop()
  }
}

function getMaxConcurrentChats(): number {
  return getLimitFromEnv()
}

export {
  acquireChatSlot,
  releaseChatSlot,
  startChatHeartbeat,
  getActiveCount,
  getMaxConcurrentChats,
}
